import { Chip as MantineChip, ChipProps, useMantineTheme } from '@mantine/core';
import { forwardRef, useMemo } from 'react';
import { usePreferencesStore } from '../../../stores';
import { defaultColorScheme } from '../../../theme/theme';
import { pickTextColorForBackground } from '../../../utils/color';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedChipProps extends ChipProps {
  gradientVariant?: GradientVariant;
  useColorScheme?: boolean; // Use color scheme from state management
}

const Chip = forwardRef<HTMLInputElement, ThemedChipProps>(
  ({ gradientVariant = 'primary', useColorScheme = true, variant = 'filled', styles, ...rest }, ref) => {
    const theme = useMantineTheme();
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
    const strongFocus = usePreferencesStore((state) => state.strongFocus);

    // Determine which color to use
    let color = theme.other.gradients[gradientVariant].from;

    if (useColorScheme) {
      const effectiveColorSchemeKey = colorSchemeKey || defaultColorScheme.key;
      const colorScheme = theme.other.colorSchemes?.find((cs: any) => cs.key === effectiveColorSchemeKey);
      if (colorScheme) {
        color = colorScheme.primary.from;
      }
    }

    // Calculate text color for checked state
    const textColor = useMemo(
      () => pickTextColorForBackground(color),
      [color]
    );

    return (
      <MantineChip
        ref={ref}
        variant={variant}
        styles={{
          ...styles,
          label: {
            transition: reducedMotion ? 'none' : 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
            '&[data-checked]': {
              backgroundColor: `${color} !important`,
              borderColor: `${color} !important`,
              color: `${textColor} !important`,
            },
            ...(styles as any)?.label,
          },
          input: {
            '&:focus + *': {
              outline: strongFocus ? `2px solid ${color} !important` : 'none',
              outlineOffset: strongFocus ? '2px' : '0',
            },
            '&:focus-within + *': {
              outline: strongFocus ? `2px solid ${color} !important` : 'none',
            },
            ...(styles as any)?.input,
          },
        }}
        {...rest}
      />
    );
  }
);

Chip.displayName = 'Chip';

export default Chip;
